import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { getLenseConfigParser } from 'parser/pricingSimulator/lenseConfigParser';
import { Scenario, ScenarioComparison } from 'types/pricingSimulator';
import { scenarioDetailsParser } from 'parser/pricingSimulator/scenarioDetailsParser';
import { comparisonTableDataParser } from 'parser/pricingAnalytics/comparisonParser';

interface FetchLenseConfigProp extends HookInputProps {
    updateComparisonProperty: <K extends keyof ScenarioComparison>(key: K, value: ScenarioComparison[K]) => void;
}

function useFetchComparisonTableData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    updateComparisonProperty,
    extras,
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setError(null);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.response?.data?.detail || error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) {
                setError(null);
            }
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { scenarioComparisonState = {} } = extras;
            if (payload && Array.isArray(payload?.data)) {
                let mergedData: any[] = [];
                const parsedData: any[] = comparisonTableDataParser(payload?.data, scenarioComparisonState);
                //not coming from infinite scroll
                if (variables?.offset > 0 && scenarioComparisonState?.tableState?.data?.length > 0) {
                    mergedData = [...scenarioComparisonState?.tableState?.data, ...parsedData];
                } else {
                    mergedData = parsedData;
                }
                updateComparisonProperty('tableState', {
                    ...scenarioComparisonState?.tableState,
                    data: mergedData,
                    count: payload?.total_count ?? 0,
                });
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        const { scenarioComparisonState = {} } = extras;
        //reset variables on error
        updateComparisonProperty('tableState', {
            ...scenarioComparisonState?.tableState,
            data: [],
            count: 0,
            offset: 0,
        });
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = { error, loading };
    return outputData;
}

export { useFetchComparisonTableData };
